import React from 'react';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VolunteerActivismOutlinedIcon from '@mui/icons-material/VolunteerActivismOutlined';
import { Box, IconButton, Link, Rating, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../../utils/responsiveMedia';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';
import SalonTemplateAbout from '../blocks/SalonTemplateAbout';
import SalonTemplateContactLocation from '../blocks/SalonTemplateContactLocation';
import SalonTemplateGallery from '../blocks/SalonTemplateGallery';
import SalonTemplateOpeningHours from '../blocks/SalonTemplateOpeningHours';
import SalonTemplateReviews from '../blocks/SalonTemplateReviews';
import SalonTemplateServices from '../blocks/SalonTemplateServices';
import SalonTemplateTeam from '../blocks/SalonTemplateTeam';


const WEEK_DAYS = Object.freeze([
  'dimanche',
  'lundi',
  'mardi',
  'mercredi',
  'jeudi',
  'vendredi',
  'samedi',
]);

const getTodayHours = (openingHours) => {
  const today = WEEK_DAYS[new Date().getDay()];
  return openingHours.find((entry) => entry?.day === today) || null;
};

const HarmonieClinicHero = ({
  identity,
  heroPhoto,
  averageRating,
  reviewCount,
  openingHours,
  hasHours,
  canBook,
  isFavorite,
  actions,
  mode,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const preview = mode === 'preview';
  const todayHours = getTodayHours(openingHours);
  const hasRating = Number.isFinite(Number(averageRating)) && reviewCount > 0;
  const hasChat = identity.raw?.client_chat_enabled === true;
  const phone = typeof identity.phone === 'string' ? identity.phone.trim() : '';
  const email = typeof identity.email === 'string' ? identity.email.trim() : '';
  const navLinkSx = {
    color: theme.salon.secondary,
    fontSize: '0.9rem',
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { color: theme.salon.primary },
    '&:focus-visible': {
      borderRadius: 1,
      outline: `3px solid ${theme.salon.focus}`,
      outlineOffset: 4,
    },
  };

  const availabilityLabel = todayHours
    ? todayHours.isClosed
      ? t('landing_search_salon.closed_today')
      : t('landing_search_salon.open_today', {
        opening: todayHours.open,
        closing: todayHours.close,
      })
    : t('landing_salon_details.opening_hours');
  const availabilityOpen = Boolean(todayHours && !todayHours.isClosed);

  return (
    <SalonPublicStyleScope
      sectionKey="header"
      component="header"
      data-testid="salon-public-hero"
      data-salon-template-block="identity"
      data-variant="institutional"
      sx={{
        width: '100%',
        color: theme.salon.secondary,
        backgroundColor: theme.salon.background,
        borderBottom: `1px solid ${theme.salon.border}`,
      }}
    >
      <Box
        component="nav"
        aria-label={t('salonTemplates.harmonieClinique.navigationLabel')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: { xs: 64, md: 78 },
          gap: { xs: 1, md: 2.5 },
          px: { xs: 1.5, sm: 3, lg: 6 },
          backgroundColor: alpha(theme.salon.background, 0.96),
          borderBottom: `1px solid ${alpha(theme.salon.secondary, 0.12)}`,
        }}
      >
        <IconButton
          aria-label={t('actions.back')}
          onClick={actions?.onBack}
          disabled={preview}
          sx={{
            color: theme.salon.secondary,
            border: `1px solid ${alpha(theme.salon.secondary, 0.18)}`,
            borderRadius: 2,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          data-salon-text-role="title"
          sx={{
            flexShrink: 1,
            minWidth: 0,
            color: theme.salon.secondary,
            fontFamily: '"Merriweather Variable", Georgia, serif',
            fontSize: { xs: '1rem', sm: '1.2rem', lg: '1.45rem' },
            fontWeight: 700,
            letterSpacing: { xs: '0.08em', md: '0.15em' },
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {identity.name}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          spacing={{ md: 2.5, lg: 4 }}
          sx={{ display: { xs: 'none', md: 'flex' }, mx: 'auto' }}
        >
          <Link href="#harmonie-services" sx={navLinkSx}>
            {t('salonTemplates.harmonieClinique.navServices')}
          </Link>
          <Link href="#harmonie-team" sx={navLinkSx}>
            {t('salonTemplates.harmonieClinique.navTeam')}
          </Link>
          {identity.description ? (
            <Link href="#harmonie-about" sx={navLinkSx}>
              {t('salonTemplates.harmonieClinique.navAbout')}
            </Link>
          ) : null}
          <Link href="#harmonie-contact" sx={navLinkSx}>
            {t('salonTemplates.harmonieClinique.navContact')}
          </Link>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          {canBook ? (
            <SalonPublicButton
              preserveLegacyStyle
              variant="contained"
              startIcon={<CalendarMonthOutlinedIcon />}
              onClick={actions?.onOpenBooking}
              disabled={preview}
              aria-disabled={String(preview)}
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                minHeight: 44,
                px: { sm: 2, lg: 2.5 },
                color: theme.salon.onPrimary,
                backgroundColor: theme.salon.primary,
                borderRadius: 2,
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: theme.colors.primary[700],
                  boxShadow: `0 8px 18px ${alpha(theme.salon.primary, 0.2)}`,
                },
              }}
            >
              {t('landing_salon_details.book_appointment')}
            </SalonPublicButton>
          ) : null}
          <IconButton
            aria-label={t('landing_salon_details.favorite')}
            onClick={actions?.onToggleFavorite}
            disabled={preview}
            sx={{
              color: isFavorite ? theme.colors.error[500] : theme.salon.secondary,
              border: `1px solid ${alpha(theme.salon.secondary, 0.18)}`,
              borderRadius: 2,
            }}
          >
            {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
          </IconButton>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 0.92fr) minmax(0, 1.08fr)' },
          minHeight: { md: 500, lg: 560 },
          overflow: 'hidden',
        }}
      >
        <Stack
          spacing={{ xs: 2, md: 2.5 }}
          justifyContent="center"
          alignItems="flex-start"
          sx={{
            position: 'relative',
            zIndex: 2,
            order: { xs: 2, md: 1 },
            px: { xs: 2.5, sm: 5, md: 6, lg: 10 },
            py: { xs: 4.5, md: 7 },
            background: `radial-gradient(circle at 8% 12%, ${alpha(theme.salon.primary, 0.08)}, transparent 34%), ${theme.salon.background}`,
          }}
        >
          <Typography
            variant="overline"
            data-salon-text-role="content"
            sx={{
              color: theme.salon.primary,
              fontWeight: 800,
              letterSpacing: '0.14em',
            }}
          >
            {t('salonTemplates.harmonieClinique.eyebrow')}
          </Typography>

          <Typography
            component="h1"
            data-salon-text-role="title"
            sx={{
              maxWidth: 680,
              color: theme.salon.secondary,
              fontFamily: '"Merriweather Variable", Georgia, serif',
              fontSize: { xs: '2.25rem', sm: '3rem', lg: '4rem' },
              fontWeight: 700,
              letterSpacing: '-0.035em',
              lineHeight: 1.12,
            }}
          >
            {identity.name}
          </Typography>

          {identity.description ? (
            <Typography
              data-salon-text-role="content"
              sx={{
                maxWidth: 590,
                color: alpha(theme.salon.secondary, 0.76),
                fontSize: { xs: '1rem', md: '1.1rem' },
                lineHeight: 1.75,
              }}
            >
              {identity.description}
            </Typography>
          ) : null}

          {hasRating ? (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Rating
                value={Number(averageRating)}
                precision={0.1}
                readOnly
                size="small"
                aria-label={t('landing_salon_details.reviews_count', { count: reviewCount })}
                sx={{ '& .MuiRating-iconFilled': { color: '#D39B43' } }}
              />
              <Typography sx={{ color: theme.salon.secondary, fontWeight: 800 }}>
                {Number(averageRating).toFixed(1)}
              </Typography>
              <Typography variant="body2" sx={{ color: alpha(theme.salon.secondary, 0.62) }}>
                {t('landing_salon_details.reviews_count_short', { count: reviewCount })}
              </Typography>
            </Stack>
          ) : null}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            useFlexGap
            sx={{ width: { xs: '100%', sm: 'auto' }, pt: 0.5 }}
          >
            {canBook ? (
              <SalonPublicButton
                preserveLegacyStyle
                variant="contained"
                startIcon={<CalendarMonthOutlinedIcon />}
                onClick={actions?.onOpenBooking}
                disabled={preview}
                aria-disabled={String(preview)}
                sx={{
                  minHeight: 50,
                  px: 3,
                  color: theme.salon.onPrimary,
                  backgroundColor: theme.salon.primary,
                  borderRadius: 2,
                  fontWeight: 700,
                  boxShadow: `0 10px 24px ${alpha(theme.salon.primary, 0.2)}`,
                  '&:hover': { backgroundColor: theme.colors.primary[700] },
                }}
              >
                {t('landing_salon_details.book_appointment')}
              </SalonPublicButton>
            ) : null}
            {hasChat ? (
              <SalonPublicButton
                preserveLegacyStyle
                variant="outlined"
                startIcon={<ChatBubbleOutlineIcon />}
                onClick={actions?.onContact}
                disabled={preview}
                aria-disabled={String(preview)}
                sx={{
                  minHeight: 50,
                  px: 3,
                  color: theme.salon.secondary,
                  backgroundColor: alpha(theme.colors.background.primary, 0.72),
                  borderColor: alpha(theme.salon.secondary, 0.42),
                  borderRadius: 2,
                  fontWeight: 700,
                  '&:hover': {
                    borderColor: theme.salon.primary,
                    backgroundColor: alpha(theme.salon.primary, 0.06),
                  },
                }}
              >
                {t('salonTemplates.harmonieClinique.contactAction')}
              </SalonPublicButton>
            ) : null}
          </Stack>

          {(hasHours || phone || email) ? (
            <Stack spacing={1.25} sx={{ pt: 1 }}>
              {hasHours ? (
                <Stack direction="row" alignItems="center" spacing={1.5} useFlexGap flexWrap="wrap">
                  <Stack
                    data-testid="harmonie-clinique-today-hours"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1.75,
                      py: 0.85,
                      color: theme.salon.secondary,
                      backgroundColor: availabilityOpen
                        ? alpha(theme.colors.success[500], 0.1)
                        : alpha(theme.salon.secondary, 0.07),
                      border: `1px solid ${availabilityOpen
                        ? alpha(theme.colors.success[600], 0.24)
                        : alpha(theme.salon.secondary, 0.14)}`,
                      borderRadius: 99,
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: availabilityOpen
                          ? theme.colors.success[600]
                          : theme.colors.gray[400],
                      }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {availabilityLabel}
                    </Typography>
                  </Stack>
                  <Link
                    href="#harmonie-opening-hours"
                    sx={{
                      ...navLinkSx,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.7,
                      borderBottom: `1px solid ${theme.salon.primary}`,
                      pb: 0.35,
                    }}
                  >
                    <AccessTimeOutlinedIcon sx={{ fontSize: '1rem' }} />
                    {t('salonTemplates.harmonieClinique.hoursLink')}
                  </Link>
                </Stack>
              ) : null}

              {(phone || email) ? (
                <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                  {phone ? (
                    <Link
                      href={preview ? undefined : `tel:${phone}`}
                      aria-disabled={String(preview)}
                      sx={{ ...navLinkSx, color: alpha(theme.salon.secondary, 0.74) }}
                    >
                      {phone}
                    </Link>
                  ) : null}
                  {email ? (
                    <Link
                      href={preview ? undefined : `mailto:${email}`}
                      aria-disabled={String(preview)}
                      sx={{ ...navLinkSx, color: alpha(theme.salon.secondary, 0.74) }}
                    >
                      {email}
                    </Link>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          ) : null}

          <SalonPublicButton
            preserveLegacyStyle
            variant="text"
            startIcon={<InfoOutlinedIcon />}
            onClick={actions?.onOpenHeaderInfo}
            disabled={preview}
            aria-disabled={String(preview)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: theme.salon.primary,
              px: 0,
            }}
          >
            {t('landing_salon_details.more_info')}
          </SalonPublicButton>
        </Stack>

        <Box
          sx={{
            position: 'relative',
            order: { xs: 1, md: 2 },
            minHeight: { xs: 260, sm: 340, md: 500 },
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${alpha(theme.salon.primary, 0.12)}, ${alpha(theme.salon.secondary, 0.2)})`,
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: {
                xs: `linear-gradient(180deg, transparent 65%, ${theme.salon.background} 100%)`,
                md: `linear-gradient(90deg, ${theme.salon.background} 0%, transparent 24%)`,
              },
              pointerEvents: 'none',
            },
          }}
        >
          {heroPhoto?.src ? (
            <Box
              component="img"
              src={heroPhoto.src}
              alt={heroPhoto.alt || identity.name}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: 'inherit',
                display: 'block',
                objectFit: 'cover',
                objectPosition: 'center',
              }}
            />
          ) : (
            <Box
              aria-hidden="true"
              sx={{
                position: 'absolute',
                inset: '12%',
                border: `1px solid ${alpha(theme.salon.primary, 0.2)}`,
                borderRadius: '48% 52% 42% 58%',
                transform: 'rotate(-6deg)',
              }}
            />
          )}
        </Box>
      </Box>
    </SalonPublicStyleScope>
  );
};

const HarmonieTrustStrip = () => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const items = [
    [GroupsOutlinedIcon, t('salonTemplates.harmonieClinique.trustTeam')],
    [VolunteerActivismOutlinedIcon, t('salonTemplates.harmonieClinique.trustServices')],
    [EventAvailableOutlinedIcon, t('salonTemplates.harmonieClinique.trustBooking')],
  ];

  return (
    <Box
      data-testid="harmonie-clinique-trust-strip"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
        my: { xs: 4, md: 6 },
        color: theme.salon.secondary,
        backgroundColor: alpha(theme.salon.primary, 0.055),
        border: `1px solid ${alpha(theme.salon.primary, 0.14)}`,
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      {items.map(([Icon, label], index) => (
        <Stack
          key={label}
          direction={{ xs: 'row', sm: 'column', lg: 'row' }}
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{
            minHeight: { xs: 76, sm: 110, lg: 94 },
            px: 2,
            py: 2,
            textAlign: { sm: 'center', lg: 'start' },
            borderInlineStart: {
              xs: 'none',
              sm: index === 0 ? 'none' : `1px solid ${alpha(theme.salon.primary, 0.16)}`,
            },
            borderTop: {
              xs: index === 0 ? 'none' : `1px solid ${alpha(theme.salon.primary, 0.16)}`,
              sm: 'none',
            },
          }}
        >
          <Icon sx={{ color: theme.salon.primary, fontSize: '1.9rem' }} />
          <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
        </Stack>
      ))}
    </Box>
  );
};

const HarmonieCliniqueTemplate = ({
  viewModel,
  actions,
  mode = 'public',
  viewport = 'desktop',
  direction = 'ltr',
}) => {
  const { layout, tokens: theme } = useSalonPublicTheme();
  const identity = viewModel?.identity || {};
  const photos = Array.isArray(viewModel?.photos) ? viewModel.photos : [];
  const reviews = Array.isArray(viewModel?.reviews) ? viewModel.reviews : [];
  const openingHours = Array.isArray(viewModel?.openingHours) ? viewModel.openingHours : [];
  const closures = Array.isArray(viewModel?.closures) ? viewModel.closures : [];
  const publicState = viewModel?.publicState || {};
  const visibility = layout.tokens.visibility;
  const mobile = viewport === 'mobile';
  const hasHours = Boolean(
    visibility.opening_hours && (openingHours.length > 0 || closures.length > 0),
  );

  return (
    <Box
      data-testid="salon-template-renderer"
      data-salon-template="harmonie_clinique"
      data-mode={mode}
      data-viewport={viewport}
      dir={direction}
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        backgroundColor: theme.salon.background,
      }}
    >
      <Box
        data-testid="harmonie-clinique-scroll-region"
        sx={{
          flex: 1,
          minHeight: mobile ? 'auto' : 0,
          width: '100%',
          overflowY: mobile ? 'visible' : 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
          [HIGH_DENSITY_TABLET_MEDIA]: { overflowY: 'visible' },
        }}
      >
        <HarmonieClinicHero
          identity={identity}
          heroPhoto={photos[0]}
          averageRating={viewModel?.averageRating}
          reviewCount={reviews.length}
          openingHours={openingHours}
          hasHours={hasHours}
          canBook={viewModel?.capabilities?.canClientBook !== false}
          isFavorite={viewModel?.capabilities?.isFavorite}
          actions={actions}
          mode={mode}
        />

        <Box
          component="main"
          data-testid="harmonie-clinique-content"
          data-layout={mobile ? 'single-column' : 'serenity-grid'}
          sx={{
            width: '100%',
            maxWidth: 1280,
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 5 },
            py: { xs: 4, md: 7 },
            boxSizing: 'border-box',
          }}
        >
          <Box id="harmonie-services" sx={{ scrollMarginTop: 24 }}>
            <SalonTemplateServices
              data={viewModel?.serviceCategories}
              variant="clinical"
              mode={mode}
              viewport={viewport}
              loading={Boolean(publicState.servicesLoading)}
              onBookService={actions?.onBookService}
            />
          </Box>

          <HarmonieTrustStrip />

          <Box id="harmonie-team" sx={{ scrollMarginTop: 24 }}>
            <SalonTemplateTeam
              data={viewModel?.team}
              variant="practitioners"
              visible={visibility.team}
              mode={mode}
              onSelectMember={actions?.onSelectTeamMember}
            />
          </Box>

          <SalonTemplateReviews
            data={{
              items: reviews,
              averageRating: viewModel?.averageRating,
              isAuthenticated: Boolean(publicState.isAuthenticated),
              isLoading: Boolean(publicState.reviewsLoading),
              error: publicState.reviewsError || null,
            }}
            variant="clinical"
            visible={visibility.reviews}
            mode={mode}
            onOpenReviewComposer={actions?.onOpenReviewComposer}
            onOpenAllReviews={actions?.onOpenAllReviews}
          />

          <Box id="harmonie-about" sx={{ scrollMarginTop: 24 }}>
            <SalonTemplateAbout
              data={identity}
              variant="panel"
              visible={Boolean(identity.description)}
            />
          </Box>

          <Box id="harmonie-opening-hours" sx={{ scrollMarginTop: 24 }}>
            <SalonTemplateOpeningHours
              data={{ hours: openingHours, closures }}
              variant="compact"
              visible={visibility.opening_hours}
              markClosuresAsBlock={false}
            />
          </Box>

          <Box id="harmonie-contact" sx={{ scrollMarginTop: 24 }}>
            <SalonTemplateContactLocation
              data={identity}
              variant="compact"
              visible={visibility.location}
              mode={mode}
            />
          </Box>

          <SalonTemplateGallery
            data={photos}
            salonId={identity.id}
            variant="secondary"
            visible={visibility.gallery}
            mode={mode}
            onSelectPhoto={actions?.onSelectPhoto}
            onNavigatePhoto={actions?.onNavigatePhoto}
            selectedIndex={publicState.photoIndex}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HarmonieCliniqueTemplate;
